import React, { useState, useEffect, useCallback, useRef } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DollarSign, TrendingUp, ChevronLeft, ChevronRight,
  Plus, Trash2, Save, Upload, Clock, Calendar,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import ExportButtons from "@/components/ExportButtons";

const OTHER_TYPES = ["Thuê vợt", "Thuê bóng", "Sân tập mini", "Nhân viên nhặt bóng", "Thuê máy bắn bóng", "Khác"];

function formatDateVN(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("vi-VN"); } catch { return d; }
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f172a] border border-[#334155] rounded px-3 py-2 text-xs">
      <p className="text-slate-400">{label}</p>
      <p className="text-lime-400 font-semibold">{formatVND(payload[0].value)}đ</p>
    </div>
  );
}

export default function RevenuePage() {
  const [mode, setMode] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("court");

  // Tab states
  const [otherEntries, setOtherEntries] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [courtDetails, setCourtDetails] = useState([]);

  // Add other revenue form
  const [newType, setNewType] = useState("Thuê vợt");
  const [newAmount, setNewAmount] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [addingOther, setAddingOther] = useState(false);

  const fileRef = useRef(null);

  const getMonth = () => selectedDate.substring(0, 7);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const param = mode === "daily" ? `date=${selectedDate}` : `month=${getMonth()}`;
      const endpoint = mode === "daily" ? "summary" : "monthly";
      const [sumRes, otherRes, noteRes] = await Promise.all([
        api.get(`/revenue/${endpoint}?${param}`),
        api.get(`/revenue/other?date=${selectedDate}`),
        api.get(`/revenue/notes?date=${selectedDate}`),
      ]);
      setSummary(sumRes.data);
      setOtherEntries(otherRes.data.entries || []);
      setNoteContent(noteRes.data.note?.content || "");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, mode]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load court details when tab switches
  useEffect(() => {
    if (activeTab === "court" && selectedDate) {
      const dateStr = mode === "daily" ? selectedDate : "";
      const startDate = mode === "monthly" ? `${getMonth()}-01` : selectedDate;
      const endDate = mode === "monthly" ? `${getMonth()}-31` : selectedDate;
      api.get(`/revenue/court-details?start_date=${startDate}&end_date=${endDate}`).then(({ data }) => {
        setCourtDetails(data.bookings || []);
      }).catch(() => {});
    }
  }, [activeTab, selectedDate, mode]);

  const navigateDate = (dir) => {
    const d = new Date(selectedDate + "T00:00:00");
    if (mode === "daily") {
      d.setDate(d.getDate() + dir);
    } else {
      d.setMonth(d.getMonth() + dir);
    }
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  const handleAddOther = async () => {
    const amount = parseInt(newAmount.replace(/\./g, "")) || 0;
    if (!newType || amount <= 0) { toast.error("Vui lòng nhập loại và số tiền hợp lệ"); return; }
    setAddingOther(true);
    try {
      await api.post("/revenue/other", { date: selectedDate, type: newType, amount, notes: newNotes });
      toast.success("Đã thêm doanh thu khác");
      setNewAmount("");
      setNewNotes("");
      loadData();
    } catch (err) {
      toast.error("Lỗi khi thêm");
    } finally {
      setAddingOther(false);
    }
  };

  const handleDeleteOther = async (id) => {
    try {
      await api.delete(`/revenue/other/${id}`);
      toast.success("Đã xóa");
      loadData();
    } catch { toast.error("Lỗi khi xóa"); }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await api.put("/revenue/notes", { date: selectedDate, content: noteContent });
      toast.success("Đã lưu ghi chú");
    } catch { toast.error("Lỗi khi lưu"); }
    finally { setSavingNote(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/revenue/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = data.errors?.length > 0
        ? `Đã import ${data.imported} mục, ${data.errors.length} lỗi`
        : `Đã import ${data.imported} mục thành công`;
      toast.success(msg);
      loadData();
    } catch (err) {
      toast.error("Lỗi khi import file");
    }
    e.target.value = "";
  };

  const displayDate = mode === "daily"
    ? formatDateVN(selectedDate)
    : new Date(selectedDate + "T00:00:00").toLocaleDateString("vi-VN", { month: "long", year: "numeric" });

  const hourlyData = (summary?.hourly_breakdown || []).map(h => ({
    hour: h.hour.substring(0, 2),
    revenue: h.revenue,
    isPeak: parseInt(h.hour) >= 16,
  }));

  const courtData = (summary?.court_breakdown || []).map(c => ({
    name: c.court_name,
    revenue: c.revenue,
    bookings: c.bookings,
  }));

  const tabs = [
    { key: "court", label: "Sân" },
    { key: "other", label: "Thu khác" },
    { key: "notes", label: "Ghi chú" },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="revenue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight">Doanh thu</h1>
        <div className="flex items-center gap-3">
          <ExportButtons endpoint={mode === "daily" ? `/export/revenue?date=${selectedDate}` : `/export/revenue?date=${selectedDate}`} label="doanh-thu" />
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 border border-[#334155] rounded-md px-3 py-1.5 transition-colors" data-testid="import-btn">
            <Upload className="w-3.5 h-3.5" /> Nhập CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleImport} />
          <div className="flex items-center gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-0.5">
            <button onClick={() => navigateDate(-1)} className="p-1.5 rounded hover:bg-[#334155] text-slate-400 hover:text-slate-200 transition-colors" data-testid="date-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 text-sm text-[#F8FAFC] font-medium min-w-[140px] text-center" data-testid="date-display">{displayDate}</span>
            <button onClick={() => navigateDate(1)} className="p-1.5 rounded hover:bg-[#334155] text-slate-400 hover:text-slate-200 transition-colors" data-testid="date-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-[130px] bg-[#0f172a] border-[#334155] text-slate-300 h-9" data-testid="mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1e293b] border-[#334155]">
              <SelectItem value="daily" className="text-slate-300">Theo ngày</SelectItem>
              <SelectItem value="monthly" className="text-slate-300">Theo tháng</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-[#1e293b] rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-cards">
          <KPICard icon={DollarSign} label="Tổng doanh thu" value={summary?.total_revenue || 0} color="lime" />
          <KPICard icon={TrendingUp} label="Thu từ sân" value={summary?.court_revenue || 0} color="sky" />
          <KPICard icon={Plus} label="Thu khác" value={summary?.other_revenue || 0} color="amber" />
          <KPICard icon={DollarSign} label="Tổng cọc" value={summary?.total_deposit || 0} color="purple" />
        </div>
      )}

      {/* Charts */}
      {!loading && summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Hourly chart */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4" data-testid="hourly-chart">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Doanh thu theo giờ</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hourlyData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#334155" }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#334155" }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" radius={[3, 3, 0, 0]}>
                  {hourlyData.map((entry, i) => (
                    <Cell key={i} fill={entry.isPeak ? "#84cc16" : "#334155"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Court chart */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4" data-testid="court-chart">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Doanh thu theo sân</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={courtData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#334155" }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#334155" }} width={55} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" fill="#38bdf8" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-1" data-testid="revenue-tabs">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? "bg-[#1e293b] text-lime-400" : "text-slate-400 hover:text-slate-200"}`}
            data-testid={`tab-${tab.key}`}>{tab.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "court" && <CourtTab courtBreakdown={summary?.court_breakdown || []} details={courtDetails} />}
      {activeTab === "other" && (
        <OtherTab
          entries={otherEntries}
          onDelete={handleDeleteOther}
          newType={newType} setNewType={setNewType}
          newAmount={newAmount} setNewAmount={setNewAmount}
          newNotes={newNotes} setNewNotes={setNewNotes}
          onAdd={handleAddOther} adding={addingOther}
        />
      )}
      {activeTab === "notes" && (
        <NotesTab
          content={noteContent}
          setContent={setNoteContent}
          onSave={handleSaveNote}
          saving={savingNote}
          date={selectedDate}
        />
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  const colorMap = {
    lime: "text-lime-400 bg-lime-500/10 border-lime-500/20",
    sky: "text-sky-400 bg-sky-500/10 border-sky-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  };
  const cls = colorMap[color] || colorMap.lime;
  return (
    <div className={`rounded-lg border p-4 ${cls}`} data-testid={`kpi-${color}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs opacity-70">{label}</span>
      </div>
      <div className="text-xl font-bold">{formatVND(value)}đ</div>
    </div>
  );
}

function CourtTab({ courtBreakdown, details }) {
  const totalBookings = courtBreakdown.reduce((s, c) => s + c.bookings, 0);
  const totalRev = courtBreakdown.reduce((s, c) => s + c.revenue, 0);

  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden" data-testid="tab-content-court">
      <Table>
        <TableHeader>
          <TableRow className="border-[#334155] hover:bg-transparent">
            <TableHead className="text-slate-400">Sân</TableHead>
            <TableHead className="text-slate-400 text-right">Số buổi</TableHead>
            <TableHead className="text-slate-400 text-right">Tổng tiền</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {courtBreakdown.length === 0 ? (
            <TableRow className="border-[#334155]">
              <TableCell colSpan={3} className="text-center py-8 text-slate-500">Không có dữ liệu</TableCell>
            </TableRow>
          ) : (
            <>
              {courtBreakdown.map((c, i) => (
                <TableRow key={i} className="border-[#334155] hover:bg-[#334155]/20">
                  <TableCell className="text-slate-200 text-sm font-medium">{c.court_name}</TableCell>
                  <TableCell className="text-slate-300 text-sm text-right">{c.bookings}</TableCell>
                  <TableCell className="text-lime-400 text-sm font-medium text-right">{formatVND(c.revenue)}đ</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-[#334155] bg-[#0f172a]/50">
                <TableCell className="text-[#F8FAFC] text-sm font-bold">Tổng</TableCell>
                <TableCell className="text-[#F8FAFC] text-sm font-bold text-right">{totalBookings}</TableCell>
                <TableCell className="text-lime-400 text-sm font-bold text-right">{formatVND(totalRev)}đ</TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function OtherTab({ entries, onDelete, newType, setNewType, newAmount, setNewAmount, newNotes, setNewNotes, onAdd, adding }) {
  const total = entries.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4" data-testid="tab-content-other">
      {/* Add form */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Thêm doanh thu ngoài</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={newType} onValueChange={setNewType}>
            <SelectTrigger className="w-full sm:w-[200px] bg-[#0f172a] border-[#334155] text-slate-200 h-9" data-testid="other-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1e293b] border-[#334155]">
              {OTHER_TYPES.map(t => <SelectItem key={t} value={t} className="text-slate-300">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="Số tiền (VNĐ)"
            className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 w-full sm:w-[140px]" data-testid="other-amount-input" />
          <Input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Ghi chú"
            className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 flex-1" />
          <Button onClick={onAdd} disabled={adding} size="sm" className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold h-9 px-4" data-testid="add-other-btn">
            <Plus className="w-4 h-4 mr-1" /> Thêm
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[#334155] hover:bg-transparent">
              <TableHead className="text-slate-400">Loại</TableHead>
              <TableHead className="text-slate-400 text-right">Số tiền</TableHead>
              <TableHead className="text-slate-400">Ghi chú</TableHead>
              <TableHead className="text-slate-400 w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow className="border-[#334155]">
                <TableCell colSpan={4} className="text-center py-8 text-slate-500">Chưa có doanh thu ngoài</TableCell>
              </TableRow>
            ) : (
              <>
                {entries.map(e => (
                  <TableRow key={e.id} className="border-[#334155] hover:bg-[#334155]/20" data-testid={`other-row-${e.id}`}>
                    <TableCell>
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs px-2 py-0.5 border">{e.type}</Badge>
                    </TableCell>
                    <TableCell className="text-amber-400 text-sm font-medium text-right">{formatVND(e.amount)}đ</TableCell>
                    <TableCell className="text-slate-400 text-sm">{e.notes}</TableCell>
                    <TableCell>
                      <button onClick={() => onDelete(e.id)} className="p-1 rounded text-slate-500 hover:text-red-400 transition-colors" data-testid={`delete-other-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-[#334155] bg-[#0f172a]/50">
                  <TableCell className="text-[#F8FAFC] text-sm font-bold">Tổng</TableCell>
                  <TableCell className="text-amber-400 text-sm font-bold text-right">{formatVND(total)}đ</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function NotesTab({ content, setContent, onSave, saving, date }) {
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4 space-y-3" data-testid="tab-content-notes">
      <h3 className="text-sm font-semibold text-slate-300">
        Ghi chú doanh thu ngày {formatDateVN(date)}
      </h3>
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Nhập ghi chú doanh thu..."
        className="bg-[#0f172a] border-[#334155] text-slate-200 min-h-[120px]"
        data-testid="revenue-note-input"
      />
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold gap-1.5" data-testid="save-note-btn">
          <Save className="w-3.5 h-3.5" /> {saving ? "Đang lưu..." : "Lưu ghi chú"}
        </Button>
      </div>
    </div>
  );
}
